export const formatCurrency = (value: string): string => {
  const numbers = value.replace(/\D/g, "");

  if (numbers === "") return "";

  const formatted = parseInt(numbers).toLocaleString("es-MX");
  return `$${formatted}`;
};

export const parseCurrency = (value: string): string => {
  return value.replace(/\D/g, "");
};

/**
 * Decimal-aware parser. Keeps digits and a single `.`, allowing at most 2
 * decimal places. Returns a plain numeric string suitable to send to the API.
 *
 * Examples:
 *   "$12,222.5"    -> "12222.5"
 *   "12,222.567"   -> "12222.56"
 *   "12222."       -> "12222."  (in-progress, preserved)
 *   ""             -> ""
 */
export const parseCurrencyDecimal = (value: string): string => {
  if (value == null) return "";

  // Keep only digits and dots.
  let cleaned = value.replace(/[^\d.]/g, "");

  if (cleaned === "") return "";

  // Collapse multiple dots into a single (first) dot.
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    const intPart = cleaned.slice(0, firstDot);
    const decPart = cleaned.slice(firstDot + 1).replace(/\./g, "");
    // Limit to at most 2 decimal places.
    cleaned = `${intPart}.${decPart.slice(0, 2)}`;
  }

  // A value with no digits (e.g. a lone ".") is not a valid amount — drop it so it can't
  // pass a truthy check and later parseFloat to NaN (→ rental_price: null on submit).
  if (!/\d/.test(cleaned)) return "";

  return cleaned;
};

/**
 * Decimal-aware formatter for display. Adds thousands separators + up to 2
 * decimals with a `$` prefix, preserving a trailing `.` or partial decimals
 * while the user is typing. Returns "" for empty.
 *
 * Examples:
 *   "12222.5"  -> "$12,222.5"
 *   "12222."   -> "$12,222."
 *   "12222"    -> "$12,222"
 *   ""         -> ""
 */
export const formatCurrencyDecimal = (value: string): string => {
  const cleaned = parseCurrencyDecimal(value == null ? "" : String(value));

  if (cleaned === "") return "";

  const dotIndex = cleaned.indexOf(".");
  const hasDot = dotIndex !== -1;

  const intDigits = (hasDot ? cleaned.slice(0, dotIndex) : cleaned).replace(
    /\D/g,
    "",
  );
  const decDigits = hasDot ? cleaned.slice(dotIndex + 1).replace(/\D/g, "") : "";

  // Format the integer part with thousands separators. Guard against an empty
  // integer part (e.g. user typed ".5").
  const intNumber = intDigits === "" ? 0 : Number(intDigits);
  const intFormatted = intNumber.toLocaleString("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  // Preserve the in-progress decimal portion instead of forcing 2 decimals.
  if (hasDot) {
    return `$${intFormatted}.${decDigits}`;
  }

  return `$${intFormatted}`;
};

export const handleCurrencyChange = (
  value: string,
  onUpdate: (numericValue: string) => void,
): void => {
  const numericValue = parseCurrency(value);
  onUpdate(numericValue);
};
